import * as THREE from 'three';
import { clamp } from '../../core/math/clamp';
import { Input } from '../../core/input/Input';
import { KeyMap } from '../../core/input/KeyMap';
import { Car } from './Car';

export class CarController {
  private readonly forward = new THREE.Vector3();
  private readonly desiredVel = new THREE.Vector3();

  constructor(private readonly car: Car, private readonly input: Input) {}

  update(dt: number) {
    const keyboardThrottle = this.input.anyDown(KeyMap.throttle) ? 1 : 0;
    const keyboardBrake = this.input.anyDown(KeyMap.brake) ? 1 : 0;
    const left = this.input.anyDown(KeyMap.left) ? 1 : 0;
    const right = this.input.anyDown(KeyMap.right) ? 1 : 0;
    const padThrottle = this.input.getThrottleAxis();
    const padBrake = this.input.getBrakeAxis();
    const throttle = Math.max(keyboardThrottle, padThrottle);
    const brake = Math.max(keyboardBrake, padBrake);
    const handbrake = this.input.anyDown(KeyMap.handbrake) || this.input.isGamepadButtonDown(1);

    const steerInput = clamp((left - right) + this.input.getSteerAxis(), -1, 1);
    this.car.steer = THREE.MathUtils.lerp(this.car.steer, steerInput, 1 - Math.pow(0.001, dt));

    const s = this.car.stats;

    const wNorm = clamp((s.weight - 1100) / (1700 - 1100), 0, 1);
    const resp = 1 - 0.18 * wNorm;

    this.forward.set(Math.sin(this.car.heading), 0, Math.cos(this.car.heading));

    // Beschleunigung nur bei Gas-Input
    if (throttle > 0) {
      this.car.speed += s.accel * resp * dt * throttle * 0.55;
    }
    
    // Bremsen
    if (brake > 0) {
      this.car.speed -= s.brake * resp * dt * (handbrake ? 1.4 : 1.0) * 1.35;
    }

    // Drag nur wenn kein Gas - realistischere Deceleration
    if (throttle <= 0 && brake <= 0) {
      // Natürliche Verlangsamung (Rollwiderstand + Luftwiderstand)
      const naturalDecel = s.drag * 0.15 * dt; // Reduzierter Drag-Faktor
      const speedSign = Math.sign(this.car.speed);
      this.car.speed -= speedSign * naturalDecel * Math.abs(this.car.speed) * 0.5;
      
      // Stopp bei sehr niedriger Geschwindigkeit
      if (Math.abs(this.car.speed) < 0.5) {
        this.car.speed = 0;
      }
    }
    
    // Maximalgeschwindigkeit - höher für Open World
    this.car.speed = clamp(this.car.speed, -s.maxSpeed * 0.35, s.maxSpeed * 1.0);

    const speed01 = clamp(Math.abs(this.car.speed) / s.maxSpeed, 0, 1);
    const turn = s.handling * (0.25 + 0.75 * speed01) * resp;
    const steerDir = this.car.speed >= 0 ? 1 : -1;
    this.car.heading += this.car.steer * turn * dt * steerDir;

    const grip = (handbrake ? s.driftGrip : s.grip) * resp;
    this.desiredVel.copy(this.forward).multiplyScalar(this.car.speed);

    const blend = 1 - Math.pow(0.001, dt * grip);
    this.car.velocity.lerp(this.desiredVel, blend);

    this.car.position.addScaledVector(this.car.velocity, dt);

    this.car.syncTransform();
  }

  addPositionOffset(dx: number, dz: number) {
    this.car.position.x += dx;
    this.car.position.z += dz;

    if (dx !== 0) this.car.velocity.x *= 0.2;
    if (dz !== 0) this.car.velocity.z *= 0.2;

    this.car.syncTransform();
  }

  getCar(): Car {
    return this.car;
  }
}
